import React, { useMemo } from 'react';
import styles from './../case-table-view.scss';

import { FavouriteDataColumn } from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { toHex } from 'ui/common/hex-converter';

import { CASE_NO_COLUMN_NAME, MAX_WIDTH_INCREASE_RATE, columnMinimumWidth } from 'app/area/case-report/case-table-view/constants';
import { Link } from 'ui/link';
import { routeConstants } from 'app/routes/route-constants';
import { ComplexValue } from 'app/types-shared';
import { Expandable, ShouldTruncateTextAndLines, standardLinesLimit, standardTextLimit } from 'ui/expandable';
import { useContentRender } from 'ui/link-heading/link-heading';
import { LimitNumberOfLines } from 'app/area/case-report/case-table-view/components/limit-number-of-lines';

type ComponentProps = {
  cells: ComplexValue<number>[];
  componentId: string;
  dateTimeFormatter?: Intl.DateTimeFormatOptions;
  locale?: string;
  column?: Partial<FavouriteDataColumn>;
  isMobielView?: boolean;
}

const getCellClassName = (cell: ComplexValue<number>, column: Partial<FavouriteDataColumn>) => {
  return useMemo(() => [
    column.dataType,
    ...((!!(cell.icon || cell) && [styles.iconColumn]) || [])
  ].join(' '), [cell, column]);
};

const getCellProperties = (cell: ComplexValue<number>) => {
  return useMemo(() => {
    const tooltip = cell.longDescription || cell.description || String(cell.value);
    const backgroundColor = cell.backgroundColor && toHex(cell.backgroundColor);
    const icon = !!cell.icon && <img src={cell.icon} alt={String(cell.value)} />;
    const colorBox = !cell.icon && !!cell.backgroundColor &&
      <div className={styles.colorBox} style={{ backgroundColor }} />;
    const cellValue = `${cell.description || cell.value}`;
    return { tooltip, icon, colorBox, cellValue };
  }, [cell]);
};

const getMinWidth = (column: Partial<FavouriteDataColumn>) => {
  return useMemo(() => {
    if (column.dataType === 'string') {
      return columnMinimumWidth.typeString;
    }
    if (column.dataType === 'date') {
      return columnMinimumWidth.typeDate;
    }
    if (column.codetable && !column.isHierarchical) { // flat single
      return columnMinimumWidth.codeTableFlat;
    }
    if (column.codetable && column.isHierarchical && !column.isMultipleSelect) {
      return columnMinimumWidth.codeTableHierarchical;
    }
    if (column.codetable && column.isHierarchical && column.isMultipleSelect) {
      return columnMinimumWidth.codeTableHierarchicalMultiple;
    }
    return 200;
  }, [column]);
};

const getCellContent = ({ cells, column, componentId, isMobielView }: ComponentProps, isSingleExpandable: boolean) => {
  return cells.map((cell, index) => {
    if (!cell.value) {
      return null;
    }
    const minBaseWidth = getMinWidth(column);
    const { tooltip, icon, colorBox, cellValue } = getCellProperties(cell);
    const shouldTruncate = !isSingleExpandable ? ShouldTruncateTextAndLines(cellValue) : false;
    const minWidth = shouldTruncate ? `${minBaseWidth + (cellValue.length * MAX_WIDTH_INCREASE_RATE)}px` : minBaseWidth;
    const contentRender = shouldTruncate ? useContentRender(cellValue, standardTextLimit, standardLinesLimit) : undefined;
    const value = column.columnName === CASE_NO_COLUMN_NAME ?
      <Link className={styles.text} url={toAbsoluteUrl(routeConstants.caseViewFor(cell.value))} target="_self">{cell.value}</Link> :
      <span className={styles.text} style={{ minWidth: minWidth && !isMobielView ? minWidth : '' }}>
        {shouldTruncate ?
          <Expandable content={contentRender} /> :
          cellValue
        }
        {index !== cells.length - 1 && <span>,</span>}
      </span>;
    return <div
      title={tooltip}
      className={getCellClassName(cell, column)}
      key={`${componentId}-${index}`}>
      {icon}
      {colorBox}
      {value}
    </div>;
  });
};

export const DisplayResultValue = ({ cells, column, componentId, isMobielView }: ComponentProps) => {
  if (!cells?.length) {
    return null;
  }
  const isCodeTableFlat = column.codetable && !column.isHierarchical;
  const limitNumberOfLines = isCodeTableFlat && column.isMultipleSelect;
  const values = getCellContent({ cells, column, componentId, isMobielView }, limitNumberOfLines);
  return <div
    key={column.columnName}
    className={styles.columnWrapper}>
    {limitNumberOfLines ?
      <LimitNumberOfLines
        elem={values}
        width={columnMinimumWidth.codeTableFlatMultiple} /> : values.map((value) => value)}
  </div>;
};

export function DisplayDateValue({ cells, componentId, locale, dateTimeFormatter, isMobielView }: ComponentProps) {
  if (!cells?.length) {
    return null;
  }
  const values = cells.map((cell) => {
    if (!cell.value) {
      return null;
    }
    const dateValue: Date = new Date(cell.value);
    if (!dateValue.getTime()) {
      return null;
    }

    const formattedDate = useMemo(() => {
      return dateValue.toLocaleDateString(locale, dateTimeFormatter);
    }, [dateValue, locale]);

    return <div
      title={formattedDate}
      key={componentId}
      className={styles.iconColumn}>
      {formattedDate}
    </div>;
  });

  return <div
    key={`${componentId}-data-column`}
    className={`${styles.columnWrapper} ${!isMobielView ? styles.dateColumn : ''}`}>
    {values.map((value) => value)}
  </div>;
}
